import React from "react";
import {
  Activity,
  CreditCard,
  DollarSign,
  FileText,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { dateFormatDynamic } from "../../../utils/datetime";
import { DateFormats } from "../../../utils/formatters";
import { NumericValue, TimeRange } from "../model/finance_model";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  formatSignedPercent,
  getTrendClasses,
  toNumber,
  useFinanceViewModel,
} from "../view_model/finance_viewmodel";

interface MetricCardProps {
  title: string;
  value: string;
  helper: string;
  icon: React.ReactNode;
  iconClass: string;
  trend?: {
    value: NumericValue;
    label: string;
  };
}

const MetricCard = ({
  title,
  value,
  helper,
  icon,
  iconClass,
  trend,
}: MetricCardProps) => (
  <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="mt-2 text-2xl font-bold text-gray-900 break-words">
          {value}
        </p>
      </div>
      <div
        className={`h-10 w-10 rounded-lg flex shrink-0 items-center justify-center ${iconClass}`}
      >
        {icon}
      </div>
    </div>
    <div className="mt-4 flex min-h-[24px] flex-wrap items-center gap-2">
      <p className="text-sm text-gray-500">{helper}</p>
      {trend && (
        <span
          className={`rounded-full px-2 py-1 text-xs font-semibold ${getTrendClasses(
            trend.value,
          )}`}
        >
          {formatSignedPercent(trend.value)} {trend.label}
        </span>
      )}
    </div>
  </div>
);

const DetailRow = ({
  label,
  value,
  valueClass = "text-gray-900",
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) => (
  <div className="flex items-center justify-between gap-4 border-b border-gray-100 py-3 last:border-b-0">
    <span className="text-sm text-gray-600">{label}</span>
    <span className={`text-right text-sm font-semibold ${valueClass}`}>
      {value}
    </span>
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
    {message}
  </div>
);

const Finance: React.FC = () => {
  const {
    state,
    dashboard,
    timeRangeOptions,
    setTimeRange,
    reloadFinanceData,
  } = useFinanceViewModel();

  if (state.loading && !dashboard) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Finance Dashboard
          </h1>
          <p className="text-gray-600">Loading financial data...</p>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-200 bg-white p-5 shadow animate-pulse"
            >
              <div className="mb-4 h-4 w-2/3 rounded bg-gray-200" />
              <div className="mb-4 h-8 w-1/2 rounded bg-gray-200" />
              <div className="h-3 w-3/4 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (state.error || !dashboard) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Finance Dashboard
          </h1>
          <p className="mt-2 text-red-600">
            {state.error || "Failed to load data"}
          </p>
        </div>
        <button
          type="button"
          onClick={reloadFinanceData}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  const {
    financeData,
    financialMetrics,
    operationalCosts,
    creditsMetrics,
    subscriptionBreakdown,
    topCompanies,
    filteredAiMetrics,
    lifetimeAiMetrics,
    tokenUsage,
    tokenAverages,
    costBreakdown,
    rates,
    revenueByTier,
    activeSubscriptionsByTier,
    totalActiveSubscriptions,
    creditUsageRate,
  } = dashboard;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Finance Dashboard
          </h1>
          <p className="text-gray-600">
            Revenue, AI costs, credits, and subscription performance
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <select
            value={state.timeRange}
            onChange={(event) => setTimeRange(event.target.value as TimeRange)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {timeRangeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={reloadFinanceData}
            disabled={state.loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              className={`h-4 w-4 ${state.loading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(financialMetrics.total_revenue)}
          helper={`${formatCurrency(
            financialMetrics.subscription_revenue,
          )} subscriptions`}
          trend={{
            value: financialMetrics.revenue_growth_rate,
            label: "growth",
          }}
          icon={<DollarSign className="h-5 w-5" />}
          iconClass="bg-green-100 text-green-700"
        />
        <MetricCard
          title="Top-Up Revenue"
          value={formatCurrency(financialMetrics.topup_revenue)}
          helper="Credit purchases in selected period"
          icon={<Wallet className="h-5 w-5" />}
          iconClass="bg-blue-100 text-blue-700"
        />
        <MetricCard
          title="Monthly Recurring Revenue"
          value={formatCurrency(financialMetrics.monthly_recurring_revenue)}
          helper={`${formatCurrency(
            financialMetrics.average_revenue_per_user,
          )} ARPU`}
          icon={<CreditCard className="h-5 w-5" />}
          iconClass="bg-indigo-100 text-indigo-700"
        />
        <MetricCard
          title="Gross Profit"
          value={formatCurrency(financialMetrics.gross_profit)}
          helper={`${formatPercent(financialMetrics.profit_margin)} margin`}
          icon={<TrendingUp className="h-5 w-5" />}
          iconClass="bg-emerald-100 text-emerald-700"
        />
        <MetricCard
          title="Customer Lifetime Value"
          value={formatCurrency(financialMetrics.customer_lifetime_value)}
          helper="Based on 24 month average"
          icon={<Users className="h-5 w-5" />}
          iconClass="bg-violet-100 text-violet-700"
        />
        <MetricCard
          title="Conversion Rate"
          value={formatPercent(financialMetrics.conversion_rate)}
          helper="Companies converted to paid"
          icon={<Activity className="h-5 w-5" />}
          iconClass="bg-amber-100 text-amber-700"
        />
        <MetricCard
          title="Churn Rate"
          value={formatPercent(financialMetrics.churn_rate)}
          helper={`${formatNumber(
            subscriptionBreakdown.cancelled_subscriptions,
          )} cancellations`}
          icon={<TrendingDown className="h-5 w-5" />}
          iconClass="bg-red-100 text-red-700"
        />
        <MetricCard
          title="AI Operating Cost"
          value={formatCurrency(operationalCosts.total_monthly_costs)}
          helper={`${formatCurrency(
            filteredAiMetrics.cost_estimate,
          )} in period`}
          icon={<FileText className="h-5 w-5" />}
          iconClass="bg-sky-100 text-sky-700"
        />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Revenue Sources
            </h2>
            <p className="text-sm text-gray-500">Selected period split</p>
          </div>
          <DetailRow
            label="Subscription revenue"
            value={formatCurrency(financialMetrics.subscription_revenue)}
          />
          <DetailRow
            label="Top-up revenue"
            value={formatCurrency(financialMetrics.topup_revenue)}
          />
          <DetailRow
            label="Total revenue"
            value={formatCurrency(financialMetrics.total_revenue)}
            valueClass="text-green-700"
          />
          <DetailRow
            label="Revenue growth"
            value={formatSignedPercent(financialMetrics.revenue_growth_rate)}
            valueClass={
              toNumber(financialMetrics.revenue_growth_rate) >= 0
                ? "text-green-700"
                : "text-red-700"
            }
          />
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Credit Utilization
            </h2>
            <p className="text-sm text-gray-500">Purchased vs used credits</p>
          </div>
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700">
                {formatPercent(creditsMetrics.credit_usage_rate)}
              </span>
              <span className="text-gray-500">usage rate</span>
            </div>
            <div className="h-3 rounded-full bg-gray-100">
              <div
                className="h-3 rounded-full bg-indigo-600"
                style={{ width: `${creditUsageRate}%` }}
              />
            </div>
          </div>
          <DetailRow
            label="Credits purchased"
            value={formatNumber(creditsMetrics.total_credits_purchased)}
          />
          <DetailRow
            label="Credits used"
            value={formatNumber(creditsMetrics.total_credits_used)}
          />
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Subscription Movement
            </h2>
            <p className="text-sm text-gray-500">Selected period changes</p>
          </div>
          <DetailRow
            label="New subscriptions"
            value={formatNumber(subscriptionBreakdown.new_subscriptions)}
            valueClass="text-green-700"
          />
          <DetailRow
            label="Cancelled subscriptions"
            value={formatNumber(subscriptionBreakdown.cancelled_subscriptions)}
            valueClass="text-red-700"
          />
          <DetailRow
            label="Active subscriptions"
            value={formatNumber(totalActiveSubscriptions)}
          />
        </section>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Revenue by Tier
            </h2>
            <p className="text-sm text-gray-500">Subscription revenue only</p>
          </div>
          {revenueByTier.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Tier
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {revenueByTier.map(([tier, revenue]) => (
                    <tr key={tier}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {tier}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                        {formatCurrency(revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState message="No tier revenue found for this period" />
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Active Subscriptions by Tier
            </h2>
            <p className="text-sm text-gray-500">Current active plans</p>
          </div>
          {activeSubscriptionsByTier.length > 0 ? (
            <div className="space-y-4">
              {activeSubscriptionsByTier.map(([tier, count]) => {
                const tierShare =
                  totalActiveSubscriptions > 0
                    ? (toNumber(count) / totalActiveSubscriptions) * 100
                    : 0;

                return (
                  <div key={tier}>
                    <div className="mb-2 flex items-center justify-between gap-4">
                      <span className="text-sm font-medium text-gray-900">
                        {tier}
                      </span>
                      <span className="text-sm font-semibold text-gray-700">
                        {formatNumber(count)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100">
                      <div
                        className="h-2 rounded-full bg-emerald-500"
                        style={{ width: `${tierShare}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState message="No active subscription tiers found" />
          )}
        </section>
      </div>

      <section className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">AI Usage</h2>
            <p className="text-sm text-gray-500">
              {costBreakdown.model || "Configured model"} processing costs and
              token usage
            </p>
          </div>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
            {financeData.time_range}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Filtered Period
            </h3>
            <DetailRow
              label="Resumes processed"
              value={formatNumber(filteredAiMetrics.resumes_processed)}
            />
            <DetailRow
              label="Cost estimate"
              value={formatCurrency(filteredAiMetrics.cost_estimate)}
            />
            <DetailRow
              label="Projected monthly cost"
              value={formatCurrency(filteredAiMetrics.projected_monthly_cost)}
            />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Lifetime Summary
            </h3>
            <DetailRow
              label="Total resumes"
              value={formatNumber(lifetimeAiMetrics.total_resumes_processed)}
            />
            <DetailRow
              label="Total cost"
              value={formatCurrency(lifetimeAiMetrics.total_cost_estimate)}
            />
            <DetailRow
              label="Average cost per resume"
              value={formatCurrency(lifetimeAiMetrics.avg_cost_per_resume, 4)}
            />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Tokens and Rates
            </h3>
            <DetailRow
              label="Total tokens"
              value={formatNumber(tokenUsage.total_tokens_used)}
            />
            <DetailRow
              label="Input / output tokens"
              value={`${formatNumber(
                tokenUsage.total_input_tokens,
              )} / ${formatNumber(tokenUsage.total_output_tokens)}`}
            />
            <DetailRow
              label="Average tokens per resume"
              value={formatNumber(tokenAverages.total)}
            />
            <DetailRow
              label="Input / output rate"
              value={`${formatCurrency(
                rates.input_cost_per_1k_tokens,
                2,
              )} / ${formatCurrency(rates.output_cost_per_1k_tokens, 2)}`}
            />
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6 shadow">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Top Revenue Companies
          </h2>
          <p className="text-sm text-gray-500">
            Highest subscription and top-up spend in the selected period
          </p>
        </div>
        {topCompanies.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Company
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Total Spent
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Transactions
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Last Payment
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Member Since
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {topCompanies.map((company, index) => (
                  <tr key={`${company.name}-${index}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {company.name || "Unknown company"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(company.total_spent)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                      {formatNumber(company.no_of_transactions)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {dateFormatDynamic(
                        company.last_payment,
                        DateFormats.fullDateTime,
                      ) || "No payment"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {company.member_since || "Unknown"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No revenue-generating companies found for this period" />
        )}
      </section>
    </div>
  );
};

export default Finance;
